import { useState, useEffect, useRef, useMemo } from 'react';
import api from '../api';

const btn = { padding: '6px 12px', borderRadius: 6, border: '1px solid rgba(128,128,128,.5)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 13 };
const btnOn = { ...btn, background: '#2563eb', borderColor: '#2563eb', color: '#fff' };

export default function MassPositions() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [sel, setSel] = useState({});
  const [ext, setExt] = useState(false);
  const [busy, setBusy] = useState(null);
  const [prog, setProg] = useState('');
  const [msg, setMsg] = useState('');
  const reqRef = useRef(null);

  useEffect(() => {
    if (!open || list) return;
    api.get('/positions/overview').then(r => {
      setList(r.data);
      const s = {}; r.data.forEach(a => { s[a.nm_id] = true; }); setSel(s);
    }).catch(e => setMsg(e.response?.data?.error || e.message));
  }, [open, list]);

  useEffect(() => {
    const h = e => { if (e.source === window && e.data && e.data.source === 'bystats-ext' && e.data.type === 'pong') setExt(true); };
    window.addEventListener('message', h);
    const ping = () => window.postMessage({ target: 'bystats-ext', type: 'ping' }, '*');
    ping(); const t = setTimeout(ping, 800);
    return () => { window.removeEventListener('message', h); clearTimeout(t); };
  }, []);

  const stats = useMemo(() => {
    const chosen = (list || []).filter(a => sel[a.nm_id]);
    const seen = new Set(), pairs = [], uniq = new Set();
    chosen.forEach(a => (a.keywords || []).forEach(k => {
      const kw = String(k).trim().toLowerCase();
      if (!kw) return;
      const key = a.nm_id + '|' + kw;
      if (seen.has(key)) return;
      seen.add(key); pairs.push({ nmId: Number(a.nm_id), keyword: kw }); uniq.add(kw);
    }));
    return { articles: chosen.length, pairs, uniq: uniq.size };
  }, [list, sel]);

  const setAll = v => { const s = {}; (list || []).forEach(a => { s[a.nm_id] = v; }); setSel(s); };

  const run = kind => {
    if (!stats.pairs.length) { setMsg('Отметь хотя бы один артикул'); return; }
    if (!ext) { setMsg('Расширение не найдено: открой bystats в Chrome с включённым расширением и обнови страницу'); return; }
    const id = 'mass' + Date.now();
    reqRef.current = id;
    const got = [];
    let adSkipped = 0, errors = 0;
    setBusy(kind); setMsg(''); setProg('запуск…');
    const finish = text => {
      window.removeEventListener('message', onMsg);
      setBusy(null); reqRef.current = null; setMsg(text);
    };
    const onMsg = async e => {
      const d = e.data;
      if (e.source !== window || !d || d.source !== 'bystats-ext' || d.id !== id) return;
      if (d.type === 'progress') setProg(`проверено ключей: ${d.done} из ${d.total}`);
      else if (d.type === 'result') {
        if (d.error) { errors++; return; }
        if (kind === 'organic') {
          if (d.isAd) { adSkipped++; return; }
          got.push({ nmId: d.nmId, keyword: d.keyword, position: d.organic ?? null, page: d.page });
        } else {
          got.push({ nmId: d.nmId, keyword: d.keyword, position: d.position ?? null, page: d.page });
        }
      } else if (d.type === 'error') { finish('Ошибка расширения: ' + d.error); }
      else if (d.type === 'done') {
        try {
          if (got.length) await api.post('/positions/save', { kind, results: got });
          let t = `Сохранено результатов: ${got.length}`;
          if (adSkipped) t += `. Пропущено ${adSkipped}: артикул показан как реклама (для органики выключи рекламу)`;
          if (d.aborted) t += `. ПРОВЕРКА ОСТАНОВЛЕНА: WB не отдал выдачу, не проверено ключей: ${d.remaining + (errors ? 1 : 0)}. Подожди и запусти позже`;
          else if (errors) t += `. Не удалось загрузить выдачу: ${errors}`;
          if (d.cancelled) t += ' (остановлено тобой)';
          finish(t + '. Результаты видны в «🔎 Позиции» у каждого артикула.');
        } catch (er) { finish('Не удалось сохранить: ' + (er.response?.data?.error || er.message)); }
      }
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ target: 'bystats-ext', type: 'check', id, items: stats.pairs }, '*');
  };

  const cancel = () => { if (reqRef.current) window.postMessage({ target: 'bystats-ext', type: 'cancel', id: reqRef.current }, '*'); };
  const worstMin = Math.ceil(stats.uniq * 5 * 3 / 60);

  return (
    <div style={{ border: '1px solid var(--border, rgba(128,128,128,.35))', borderRadius: 10, padding: '8px 14px', marginBottom: 12, background: 'var(--surface, transparent)' }}>
      <div onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ opacity: .7 }}>{open ? '▾' : '▸'}</span> 🔎 Массовая проверка позиций
        <span style={{ fontWeight: 400, opacity: .6, fontSize: 12 }}>один поиск на ключ сразу для всех отмеченных артикулов</span>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          {!list ? <div style={{ fontSize: 13, opacity: .7 }}>{msg || 'Загрузка…'}</div> : list.length === 0 ? (
            <div style={{ fontSize: 13, opacity: .7 }}>Ни у одного артикула нет внесённых ключей. Добавь их в «🔎 Позиции» у карточки.</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button style={btn} onClick={() => setAll(true)}>Выбрать все</button>
                <button style={btn} onClick={() => setAll(false)}>Снять все</button>
                <span style={{ fontSize: 12, opacity: .75 }}>
                  Артикулов: {stats.articles}, ключей всего: {stats.pairs.length}, <b>уникальных: {stats.uniq}</b> (поисков), страниц не больше {stats.uniq * 5}, по времени до ~{worstMin} мин
                </span>
              </div>
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid rgba(128,128,128,.25)', borderRadius: 6, padding: 6 }}>
                {list.map(a => (
                  <label key={a.nm_id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, padding: '2px 4px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!sel[a.nm_id]} onChange={e => setSel(s => ({ ...s, [a.nm_id]: e.target.checked }))} />
                    <span style={{ fontWeight: 600 }}>{a.vendor_code || a.nm_id}</span>
                    <span style={{ opacity: .6, fontSize: 12 }}>{a.nm_id} · ключей: {(a.keywords || []).length}</span>
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button style={busy === 'organic' ? btnOn : btn} disabled={!!busy} onClick={() => run('organic')}>🌿 Проверить органику</button>
                <button style={busy === 'ad' ? btnOn : btn} disabled={!!busy} onClick={() => run('ad')}>📢 Проверить рекламу</button>
                {busy && <button style={btn} onClick={cancel}>Стоп</button>}
                <span style={{ fontSize: 12, opacity: .7 }}>{busy ? prog : (ext ? 'расширение подключено' : 'расширение не найдено')}</span>
              </div>
            </>
          )}
          {list && msg && <div style={{ marginTop: 8, fontSize: 13 }}>{msg}</div>}
        </div>
      )}
    </div>
  );
}
